import { Button, ButtonLink } from '@postscript/components';

import { useGlobalModal } from 'components/GlobalModal/globalModal';
import { useUser } from 'controllers/contexts/user';
import { useProgressiveOnboarding } from 'providers/ProgressiveOnboarding/ProgressiveOnboardingProvider';

import { BRAND_CENTER_PATH } from 'components/brand/constants';
import ShopperDemoModal from 'components/shopper/components/modals/ShopperDemoModal';
import { SHOPPER_CONFIG_BASE_PATH } from 'components/shopper/constants';
import type { WorkflowContent } from 'providers/ProgressiveOnboarding/types';
import { logButtonClickEvent, PRODUCT_AREAS } from 'utils/events';

import {
  ACTIVATE_SHOPPER_STEP_KEY,
  BRAND_CENTER_SAVED_STEP_KEY,
  CONFIGURE_SHOPPER_STEP_KEY,
  DEMO_SHOPPER_STEP_KEY,
  PROGRESSIVE_ONBOARDING_SOURCE,
  SHOPPER_FEATURE_KEY,
} from './constants';

export const SHOPPER_STEPS: WorkflowContent['steps'] = {
  // [LEARN_ABOUT_SHOPPER_STEP_KEY]: {
  //   key: LEARN_ABOUT_SHOPPER_STEP_KEY,
  //   guideKey: SHOPPER_FEATURE_KEY,
  //   title: 'Learn About Shopper',
  //   content:
  //     'Understand how Shopper can help your business grow with AI-powered customer assistance.',
  //   icon: 'Book2',
  //   PrimaryAction: ({ step }) => {
  //     const { markStepCompleted } = useProgressiveOnboarding();
  //     const { showModal } = useGlobalModal();
  //     const { user } = useUser();

  //     return (
  //       <Button
  //         disabled={step.state === 'blocked'}
  //         onClick={(event) => {
  //           showModal({
  //             children: <ShopperLearnMoreModal />,
  //             style: { width: 1130, padding: 0 },
  //           });

  //           markStepCompleted({
  //             guideKey: SHOPPER_FEATURE_KEY,
  //             stepKey: LEARN_ABOUT_SHOPPER_STEP_KEY,
  //           });

  //           logButtonClickEvent(event, {
  //             productArea: PRODUCT_AREAS.SHOPPER,
  //             source: PROGRESSIVE_ONBOARDING_SOURCE,
  //             workflow: SHOPPER_FEATURE_KEY,
  //             step: LEARN_ABOUT_SHOPPER_STEP_KEY,
  //             shop_id: user.shop_id,
  //           });
  //         }}
  //       >
  //         Learn More
  //       </Button>
  //     );
  //   },
  //   subSteps: [
  //     'Watch overview video',
  //     'Read feature documentation',
  //     'Understand key benefits',
  //   ],
  // },
  [BRAND_CENTER_SAVED_STEP_KEY]: {
    key: BRAND_CENTER_SAVED_STEP_KEY,
    guideKey: SHOPPER_FEATURE_KEY,
    title: 'Set Up Voice & Tone',
    content:
      'Make sure your AI sounds like your brand before you demo or activate Shopper.',
    icon: 'Sparkles',
    PrimaryAction: ({ step }) => {
      const { user } = useUser();

      return (
        <ButtonLink
          disabled={step.state === 'blocked'}
          to={BRAND_CENTER_PATH}
          onClick={(event) => {
            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.SHOPPER,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: SHOPPER_FEATURE_KEY,
              step: BRAND_CENTER_SAVED_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
        >
          Go To Brand Center
        </ButtonLink>
      );
    },
    subSteps: [
      'Pre-filled for quick setup',
      'Review and make edits if needed',
      "Save and you're set",
    ],
  },
  [DEMO_SHOPPER_STEP_KEY]: {
    key: DEMO_SHOPPER_STEP_KEY,
    guideKey: SHOPPER_FEATURE_KEY,
    title: 'Demo Shopper',
    content:
      'Text your custom Shopper and see how your AI responds before customers do.',
    icon: 'Iphone',
    PrimaryAction: ({ step }) => {
      const { showModal } = useGlobalModal();
      const { user } = useUser();

      return (
        <Button
          disabled={step.state === 'blocked'}
          onClick={(event) => {
            showModal({
              children: <ShopperDemoModal />,
              style: { width: 1070, padding: 0 },
            });

            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.SHOPPER,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: SHOPPER_FEATURE_KEY,
              step: DEMO_SHOPPER_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
        >
          Try Demo
        </Button>
      );
    },
    SecondaryAction: ({ step }) => {
      const { user } = useUser();
      const { markStepCompleted } = useProgressiveOnboarding();

      if (step.state === 'incomplete') {
        return (
          <Button
            variant="text"
            onClick={(event) => {
              markStepCompleted({
                guideKey: step.guideKey,
                stepKey: step.key,
                metadata: { skipped: new Date().toISOString() },
              });

              logButtonClickEvent(event, {
                productArea: PRODUCT_AREAS.SHOPPER,
                source: PROGRESSIVE_ONBOARDING_SOURCE,
                workflow: step.guideKey,
                step: step.key,
                shop_id: user.shop_id,
              });
            }}
          >
            Skip for Now
          </Button>
        );
      }

      return null;
    },
    subSteps: [
      'Try asking common product or support questions',
      'Test typos, weird phrasing, or off-topic messages',
      'Update Tone & Voice or Configure Shopper to adjust',
    ],
  },
  [CONFIGURE_SHOPPER_STEP_KEY]: {
    key: CONFIGURE_SHOPPER_STEP_KEY,
    guideKey: SHOPPER_FEATURE_KEY,
    title: 'Configure Shopper',
    content:
      'Set up what Shopper says, how it responds, and what it should avoid.',
    icon: 'Settings2',
    PrimaryAction: ({ step }) => {
      const { user } = useUser();

      return (
        <ButtonLink
          disabled={step.state === 'blocked'}
          to={SHOPPER_CONFIG_BASE_PATH}
          onClick={(event) => {
            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.SHOPPER,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: SHOPPER_FEATURE_KEY,
              step: CONFIGURE_SHOPPER_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
        >
          Configure Shopper
        </ButtonLink>
      );
    },
    subSteps: [
      'Customize messages: add an offer, update FAQs, and review outreach and support details',
      "Manage content: exclude products Shopper shouldn't mention",
    ],
  },
  [ACTIVATE_SHOPPER_STEP_KEY]: {
    key: ACTIVATE_SHOPPER_STEP_KEY,
    guideKey: SHOPPER_FEATURE_KEY,
    title: 'Activate Shopper',
    content: 'Turn on Shopper to start helping customers and tracking results.',
    icon: 'Power',
    PrimaryAction: ({ step }) => {
      const { user } = useUser();

      return (
        <ButtonLink
          disabled={step.state === 'blocked'}
          to={SHOPPER_CONFIG_BASE_PATH}
          onClick={(event) => {
            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.SHOPPER,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: SHOPPER_FEATURE_KEY,
              step: ACTIVATE_SHOPPER_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
        >
          Go To Activation
        </ButtonLink>
      );
    },
    subSteps: [
      'Click Activate to go live',
      'Add to your plan or start a trial if prompted',
      'Start seeing real conversations and delivering faster responses to customers',
    ],
  },
};
